// Camada de abstração para WhatsApp messaging. Hoje wrapped em Meta Cloud
// API via send-whatsapp edge function. Continua swappable sem tocar em
// hooks ou components.
//
// Padrão estabelecido em src/services/README.md:
// - sem React
// - throw raw errors
// - orgId explícito quando relevante
// - thin translation layer (snake_case do DB → camelCase do TS)

import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export interface WhatsAppChannel {
  id: string;
  phoneNumber: string;
  displayName: string | null;
  status: 'pending' | 'connected' | 'disconnected' | 'error';
  lastError: string | null;
}

export interface SendMessageParams {
  /** WhatsApp ID — E.164 digits without leading +. Example: '351912345678'. */
  to: string;
  /** Plain text body. */
  body: string;
}

export interface SendMessageResult {
  /** BSP message ID (Cloud API id, e.g. 'wamid.HBgM…'). */
  messageId: string;
}

export class WhatsAppNotConfiguredError extends Error {
  constructor() {
    super('WhatsApp channel not configured for this organization');
    this.name = 'WhatsAppNotConfiguredError';
  }
}

/**
 * Envia mensagem WhatsApp via edge function send-whatsapp. NÃO insere
 * em `mensagens` — sub-projeto 4 fará a persistência atomicamente
 * antes de chamar esta function.
 */
export async function sendMessage(params: SendMessageParams): Promise<SendMessageResult> {
  const { data, error } = await supabase.functions.invoke('send-whatsapp', {
    body: params,
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return { messageId: data.messageId };
}

/**
 * Lê o canal WhatsApp da org actual via RLS. null se não configurado.
 * Throws (não retorna null) em erro de DB para evitar mascarar problemas
 * reais como "sem canal".
 */
export async function getChannel(orgId: string): Promise<WhatsAppChannel | null> {
  const { data, error } = await supabase
    .from('whatsapp_channels')
    .select('id, phone_number, display_name, status, last_error')
    .eq('organization_id', orgId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  return {
    id: data.id,
    phoneNumber: data.phone_number,
    displayName: data.display_name,
    status: data.status as WhatsAppChannel['status'],
    lastError: data.last_error,
  };
}

export interface WhatsAppHealth {
  /** A org tem um canal configurado? */
  configured: boolean;
  /** O token + número funcionam mesmo contra a Meta agora? */
  healthy: boolean;
  /** Dados ao vivo da Meta (presentes quando healthy). */
  displayPhoneNumber: string | null;
  verifiedName: string | null;
  qualityRating: string | null;
  /** Erro real da Meta (presente quando !healthy). */
  errorCode: string | null;
  errorMessage: string | null;
}

/**
 * Verifica em tempo real a saúde do canal WhatsApp via edge function
 * whatsapp-health (probe GET à Meta com o token do tenant). Ao contrário de
 * getChannel().status (flag estático), isto reflete se a ligação funciona
 * mesmo agora. Throws em erro de transporte/edge; o estado lógico (configured/
 * healthy/erro Meta) vem no corpo.
 */
export async function checkWhatsAppHealth(): Promise<WhatsAppHealth> {
  const { data, error } = await supabase.functions.invoke('whatsapp-health', {
    body: {},
  });
  if (error) throw error;
  return {
    configured: data?.configured ?? false,
    healthy: data?.healthy ?? false,
    displayPhoneNumber: data?.displayPhoneNumber ?? null,
    verifiedName: data?.verifiedName ?? null,
    qualityRating: data?.qualityRating ?? null,
    errorCode: data?.errorCode ?? null,
    errorMessage: data?.errorMessage ?? null,
  };
}

export interface WhatsAppContact {
  id: string;
  clientePotencialId: string;
  waId: string;
  nomeDeclarado: string | null;
  optedIn: boolean;
  ultimaMsgEm: string | null;
}

/**
 * Lê o whatsapp_contact de um cliente. null se cliente nunca enviou WhatsApp
 * para a org. Throws em erro de DB.
 */
export async function getWhatsAppContact(
  clienteId: string,
): Promise<WhatsAppContact | null> {
  const { data, error } = await supabase
    .from('whatsapp_contacts')
    .select('id, cliente_potencial_id, wa_id, nome_declarado, opted_in, ultima_msg_em')
    .eq('cliente_potencial_id', clienteId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    id: data.id,
    clientePotencialId: data.cliente_potencial_id,
    waId: data.wa_id,
    nomeDeclarado: data.nome_declarado,
    optedIn: data.opted_in,
    ultimaMsgEm: data.ultima_msg_em,
  };
}

export interface SendWhatsAppMessageParams {
  clienteId: string;
  body: string;
  replyToBspId?: string;
}

export interface SendWhatsAppMessageResult {
  mensagemId: string;
  bspMessageId: string;
}

/**
 * Envia mensagem WhatsApp via edge function send-whatsapp.
 *
 * O edge function faz INSERT em `mensagens` ANTES de chamar Meta Cloud API,
 * então mesmo em caso de erro 502 a mensagem fica visível no chat com
 * status_entrega='failed'. O `mensagemId` é retornado em sucesso e em
 * erro (anexado à instância de Error como propriedade `mensagemId`).
 */
export async function sendWhatsAppMessage(
  params: SendWhatsAppMessageParams,
): Promise<SendWhatsAppMessageResult> {
  const { data, error } = await supabase.functions.invoke('send-whatsapp', {
    body: params,
  });
  if (error) throw error;
  if (!data) {
    throw new Error('send-whatsapp returned no data');
  }
  if (data.error) {
    const e = new Error(data.error) as Error & { mensagemId?: string };
    if (data.mensagemId) e.mensagemId = data.mensagemId;
    throw e;
  }
  return {
    mensagemId: data.mensagemId,
    bspMessageId: data.bspMessageId,
  };
}

/**
 * Subscribe a postgres_changes na tabela `mensagens` filtrada por cliente.
 * Chama `onChange` quando há INSERT/UPDATE/DELETE — caller deve invalidar
 * a React Query relevante. Retorna o channel; caller é responsável por
 * `unsubscribeMensagensChannel(ch)` ao desmontar.
 */
export function subscribeToMensagensChanges(
  clienteId: string,
  onChange: () => void,
): RealtimeChannel {
  const channel = supabase
    .channel(`mensagens:${clienteId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'mensagens',
        filter: `cliente_potencial_id=eq.${clienteId}`,
      },
      () => onChange(),
    )
    .subscribe();
  return channel;
}

export function unsubscribeMensagensChannel(channel: RealtimeChannel): void {
  void supabase.removeChannel(channel);
}

/**
 * Subscrição realtime do inbox: escuta TODAS as mudanças (`*`) de mensagens da
 * org (qualquer conversa) — INSERT mantém a lista viva sem chat aberto, UPDATE
 * apanha o marcar-lido (para contadores de não-lidas). `channelName` permite a
 * vários consumidores (inbox + badge) subscreverem sem partilhar o mesmo tópico.
 */
export function subscribeToInboxChanges(
  orgId: string,
  onChange: () => void,
  channelName = 'inbox-realtime',
): RealtimeChannel {
  const channel = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'mensagens',
        filter: `organization_id=eq.${orgId}`,
      },
      () => onChange(),
    )
    .subscribe();
  return channel;
}

export interface ConnectWhatsAppParams {
  /** OAuth code retornado pelo FB.login callback */
  code: string;
  /** Phone number ID retornado pelo Embedded Signup wizard */
  metaPhoneNumberId: string;
  /** WhatsApp Business Account ID retornado pelo Embedded Signup wizard */
  metaWabaId: string;
}

export interface ConnectWhatsAppResult {
  channelId: string;
  phoneNumber: string;
  status: 'connected';
}

/**
 * Liga o WhatsApp da org via Embedded Signup. Chamada após o Meta JS SDK
 * retornar code + phone_number_id + waba_id. O edge function trata da
 * troca de token + subscribe webhook + persistência.
 */
export async function connectWhatsApp(
  params: ConnectWhatsAppParams,
): Promise<ConnectWhatsAppResult> {
  const { data, error } = await supabase.functions.invoke('whatsapp-connect', {
    body: {
      code: params.code,
      phone_number_id: params.metaPhoneNumberId,
      waba_id: params.metaWabaId,
    },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return {
    channelId: data.channel_id,
    phoneNumber: data.phone_number,
    status: 'connected',
  };
}

/**
 * Desliga o WhatsApp da org. Apaga a row + (best-effort) unsubscribe
 * webhook no Meta + cleanup do vault secret. Histórico de mensagens
 * preserva-se.
 */
export async function disconnectWhatsApp(): Promise<{ disconnected: true }> {
  const { data, error } = await supabase.functions.invoke('whatsapp-disconnect', {
    body: {},
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return { disconnected: true };
}

export interface SendOSTemplateResult {
  /** Set when the template was sent successfully. */
  mensagemId?: string;
  /** Meta message id (wamid.…) when sent. */
  bspMessageId?: string;
  /** True when no message was sent (missing data, invoke error, backend error). */
  skipped?: true;
  /** Free-form reason for the skip (for debug logging). */
  skipReason?: string;
}

/**
 * Disparar o envio do template de OS concluída via Meta Cloud API.
 *
 * Contrato: NUNCA throws. Falhas são silenciosas — o caller fica com
 * `{ skipped: true, skipReason }` e pode logar via console.warn. Errors
 * server-side são capturados via Sentry no edge function.
 */
export async function sendOSTemplate(osId: string): Promise<SendOSTemplateResult> {
  const { data, error } = await supabase.functions.invoke('send-os-template', {
    body: { os_id: osId },
  });
  if (error) {
    console.warn('[sendOSTemplate] invoke error (non-blocking)', error);
    return { skipped: true, skipReason: 'invoke_error' };
  }
  if (data?.error) {
    console.warn('[sendOSTemplate] backend error (non-blocking)', data.error);
    return { skipped: true, skipReason: String(data.error) };
  }
  if (data?.skipped) {
    return { skipped: true, skipReason: data.reason };
  }
  return { mensagemId: data?.mensagemId, bspMessageId: data?.bspMessageId };
}

/**
 * Cria um signed URL (1h) para um ficheiro de media no bucket privado
 * indicado. Por defeito usa `whatsapp-media`; passa `'app-chat-media'` para
 * media de mensagens do canal 'app'. A RLS de Storage só deixa assinar media
 * da própria org.
 */
export async function getWhatsAppMediaUrl(
  storagePath: string,
  bucket: string = 'whatsapp-media',
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(storagePath, 3600);
  if (error) throw error;
  return data.signedUrl;
}

export interface RetryWhatsAppMediaResult {
  mediaStatus: 'ready' | 'failed';
}

/**
 * Pede à edge function whatsapp-media-retry para re-descarregar a media de
 * uma mensagem (modo single). Usado pelo botão "tentar novamente" do Painel.
 */
export async function retryWhatsAppMedia(
  mensagemId: string,
): Promise<RetryWhatsAppMediaResult> {
  const { data, error } = await supabase.functions.invoke('whatsapp-media-retry', {
    body: { mensagemId },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return { mediaStatus: data.media_status };
}

export interface SendMediaParams {
  clienteId: string;
  storagePath: string;
  mimeType: string;
  filename: string;
  caption?: string;
  tipoMedia: 'image' | 'document' | 'audio';
}

/**
 * Faz upload do ficheiro para o bucket whatsapp-media em {orgId}/{uuid}.{ext}
 * (RLS permite o próprio org). Devolve o storagePath para o send-whatsapp ler.
 */
export async function uploadOutboundMedia(
  orgId: string,
  file: File,
): Promise<{ storagePath: string }> {
  const ext = file.name.includes('.') ? file.name.split('.').pop()! : 'bin';
  const storagePath = `${orgId}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage
    .from('whatsapp-media')
    .upload(storagePath, file, { contentType: file.type });
  if (error) throw error;
  return { storagePath };
}

/** Envia media (imagem/documento) via edge function send-whatsapp. */
export async function sendWhatsAppMedia(
  params: SendMediaParams,
): Promise<{ mensagemId: string; bspMessageId: string | null }> {
  const { data, error } = await supabase.functions.invoke('send-whatsapp', {
    body: {
      clienteId: params.clienteId,
      media: {
        storagePath: params.storagePath,
        mimeType: params.mimeType,
        filename: params.filename,
        caption: params.caption,
        tipoMedia: params.tipoMedia,
      },
    },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return { mensagemId: data.mensagemId, bspMessageId: data.bspMessageId ?? null };
}

export interface IniciarConversaParams {
  nome: string;
  telefone: string;
}

export interface IniciarConversaResult {
  clienteId: string;
  bspMessageId?: string;
  alreadyExisted: boolean;
  sendFailed: boolean;
  error?: string;
}

/**
 * Inicia uma conversa WhatsApp com um potencial cliente. Chamada pelo form
 * "Conversa Nova" do Painel. O edge function `iniciar-conversa` faz:
 * - Criar ou atualizar o cliente potencial + whatsapp_contact
 * - Enviar mensagem inicial via Meta Cloud API
 * - Registar em `mensagens` (status_entrega='enviada' ou 'failed')
 * - Retornar clienteId e bspMessageId
 *
 * Contrato: throws em erro de transporte ou error payload. Caller trata
 * erros com toast.error(sanitizeSupabaseError(e)).
 */
export async function iniciarConversa(
  params: IniciarConversaParams,
): Promise<IniciarConversaResult> {
  const { data, error } = await supabase.functions.invoke('iniciar-conversa', {
    body: { nome: params.nome, telefone: params.telefone },
  });
  if (error) throw error;
  if (!data) throw new Error('iniciar-conversa returned no data');
  // Hard backend error (sem clienteId) → throw. O caso soft `sendFailed`
  // também traz `error`, mas com `clienteId` — esse devolve-se normalmente.
  if (data.error && !data.clienteId) throw new Error(data.error);
  return {
    clienteId: data.clienteId,
    bspMessageId: data.bspMessageId,
    alreadyExisted: data.alreadyExisted ?? false,
    sendFailed: data.sendFailed ?? false,
    error: data.error,
  };
}

/**
 * Marca a conversa como lida na Meta (status:read da última msg do cliente) →
 * o cliente vê os tiques azuis. Best-effort: nunca faz throw (é cortesia).
 */
export async function markWhatsAppRead(bspMessageId: string): Promise<void> {
  try {
    const { error } = await supabase.functions.invoke('whatsapp-mark-read', {
      body: { bspMessageId },
    });
    if (error) {
      console.warn('[markWhatsAppRead] invoke error (não-bloqueante)', error);
    }
  } catch (e) {
    console.warn('[markWhatsAppRead] falhou (não-bloqueante)', e);
  }
}

/**
 * Envia o indicador "a escrever..." ao cliente (reutiliza whatsapp-mark-read
 * com typing:true). Best-effort: nunca faz throw (é cortesia, fire-and-forget).
 */
export async function sendTypingIndicator(bspMessageId: string): Promise<void> {
  try {
    const { error } = await supabase.functions.invoke('whatsapp-mark-read', {
      body: { bspMessageId, typing: true },
    });
    if (error) {
      console.warn('[sendTypingIndicator] invoke error (não-bloqueante)', error);
    }
  } catch (e) {
    console.warn('[sendTypingIndicator] falhou (não-bloqueante)', e);
  }
}

export interface SendReactionParams {
  clienteId: string;
  bspMessageId: string;
  emoji: string; // '' remove a reação
}

/**
 * Envia uma reação (emoji) a uma mensagem do cliente. Best-effort: nunca throw.
 */
export async function sendReaction(params: SendReactionParams): Promise<void> {
  try {
    const { error } = await supabase.functions.invoke('send-whatsapp-reaction', {
      body: { clienteId: params.clienteId, bspMessageId: params.bspMessageId, emoji: params.emoji },
    });
    if (error) console.warn('[sendReaction] invoke error (não-bloqueante)', error);
  } catch (e) {
    console.warn('[sendReaction] falhou (não-bloqueante)', e);
  }
}
